import React, { useRef, useState } from "react";
import Swal from "sweetalert2";
import {
  PresupuestoExportar,
  PresupuestoImportar,
} from "../../service/presupuestoExcel";

/**
 * Importar y descargar el presupuesto de un proyecto en Excel.
 *
 * Importar reemplaza el presupuesto que ya tenga el proyecto, así que se
 * pregunta antes: es la clase de acción que no se puede deshacer.
 */
export default function BotonesExcel({ proyecto, token, onImportado, ocultarDescargar }) {
  const inputArchivo = useRef(null);
  // Qué está haciendo: null, "importar" o "descargar". Sirve para deshabilitar
  // los dos botones y para poner el spinner sólo en el que se apretó.
  const [tarea, setTarea] = useState(null);
  const trabajando = tarea !== null;

  const elegirArchivo = () => inputArchivo.current?.click();

  const alElegir = async (evento) => {
    const archivo = evento.target.files?.[0];
    // Se limpia para que elegir el mismo archivo otra vez vuelva a disparar.
    evento.target.value = "";

    if (!archivo) return;

    const confirmacion = await Swal.fire({
      icon: "warning",
      title: "¿Reemplazar el presupuesto?",
      html:
        `Vas a cargar <b>${archivo.name}</b> en <b>${proyecto.nombre}</b>.<br><br>` +
        "El presupuesto que tenga el proyecto se reemplaza por el del archivo.",
      showCancelButton: true,
      confirmButtonText: "Sí, importar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#2f3a34",
    });

    if (!confirmacion.isConfirmed) return;

    setTarea("importar");

    const htmlProgreso = (porcentaje) =>
      `<div class="progress" style="height: 10px">` +
      `<div class="progress-bar bg-dark" role="progressbar" style="width: ${porcentaje}%" ` +
      `aria-valuenow="${porcentaje}" aria-valuemin="0" aria-valuemax="100"></div>` +
      `</div>` +
      `<div class="mt-2 small text-muted">` +
      (porcentaje < 100 ? `Subiendo... ${porcentaje}%` : "Leyendo el Excel...") +
      `</div>`;

    Swal.fire({
      title: "Importando presupuesto",
      html: htmlProgreso(0),
      allowOutsideClick: false,
      showConfirmButton: false,
    });

    try {
      const respuesta = await PresupuestoImportar(
        proyecto.id,
        archivo,
        token,
        (porcentaje) => Swal.update({ html: htmlProgreso(porcentaje) })
      );

      if (!respuesta?.success) {
        throw new Error(respuesta?.message || "No se pudo importar el archivo");
      }

      const r = respuesta.data || {};
      Swal.fire({
        icon: "success",
        title: "Presupuesto importado",
        html:
          `<div style="text-align:left">` +
          `${r.cuentas ?? 0} cuentas<br>` +
          `${(r.subcuentas ?? 0) + (r.subgrupos ?? 0) + (r.conceptos ?? 0)} partidas<br>` +
          `${r.aportantes ?? 0} aportantes con ${r.aportaciones ?? 0} aportaciones<br>` +
          `${r.informes ?? 0} informes semestrales<br>` +
          `${r.periodos ?? 0} periodos de flujo` +
          `</div>`,
        confirmButtonColor: "#2f3a34",
      });

      if (onImportado) onImportado();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "No se pudo importar",
        text: error.message,
      });
    } finally {
      setTarea(null);
    }
  };

  const descargar = async () => {
    setTarea("descargar");
    try {
      await PresupuestoExportar(proyecto.id, token);
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo descargar", text: error.message });
    } finally {
      setTarea(null);
    }
  };

  return (
    <div className="d-flex gap-2">
      <input
        ref={inputArchivo}
        type="file"
        accept=".xlsx,.xls"
        className="d-none"
        onChange={alElegir}
      />

      <button
        type="button"
        className="crud-secondary-btn"
        onClick={elegirArchivo}
        disabled={trabajando}
        title="Cargar el presupuesto desde un Excel"
      >
        {tarea === "importar" ? (
          <span className="spinner-boton" aria-hidden="true" />
        ) : (
          <i className="bi bi-upload" aria-hidden="true"></i>
        )}
        Importar
      </button>

      {!ocultarDescargar && (
        <button
          type="button"
          className="crud-secondary-btn"
          onClick={descargar}
          disabled={trabajando}
          title="Descargar el presupuesto en Excel"
        >
          {tarea === "descargar" ? (
            <span className="spinner-boton" aria-hidden="true" />
          ) : (
            <i className="bi bi-download" aria-hidden="true"></i>
          )}
          {tarea === "descargar" ? "Generando..." : "Excel"}
        </button>
      )}
    </div>
  );
}
